import React from 'react';
import PropTypes from 'prop-types';
import { withStyles } from '@material-ui/core/styles';
import { Divider, Typography } from '@material-ui/core';
import { formatMoney } from '../../helpers/invoiceHelpers';
import { formatPercent, formatRates } from './refundFormat';
import { keys } from './constants';

// Per-rate VAT breakdown + total to credit. Display only - the BE recomputes the avoir.
// Mono-tax lines split TTC-first (HT + VAT); multi-tax lines show TTC only and are excluded
// from the HT/VAT grand totals (which show only when the selection is fully mono-tax).
const styles = ({ spacing: { unit }, palette }) => ({
  overline: {
    textTransform: 'uppercase',
    letterSpacing: '0.04em',
    marginTop: unit,
    display: 'block',
  },
  row: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'baseline',
    gap: `${unit * 2}px`,
    marginTop: unit / 2,
  },
  value: {
    fontVariantNumeric: 'tabular-nums',
  },
  totalValue: {
    fontVariantNumeric: 'tabular-nums',
    color: palette.primary.main,
    fontWeight: 700,
  },
  divider: {
    marginTop: unit,
    marginBottom: unit,
  },
});

const RefundSummary = ({ classes, translate, summary, locale, currency }) => {
  const { byRate, multiTax, totalHT, totalVAT, totalTTC, hasMultiTax } = summary;
  const money = value => formatMoney(value, locale, currency);
  const hasAny = byRate.length > 0 || multiTax.length > 0;

  const row = (key, label, value, strong) => (
    <div key={key} className={classes.row}>
      <Typography variant={strong ? 'subheading' : 'body2'}>{label}</Typography>
      <Typography variant={strong ? 'subheading' : 'body2'} className={strong ? classes.totalValue : classes.value}>
        {value}
      </Typography>
    </div>
  );

  return (
    <div>
      <Typography variant="subheading">{translate(keys.summaryTitle)}</Typography>
      <Typography variant="caption" color="textSecondary" className={classes.overline}>
        {translate(keys.vatBreakdown)}
      </Typography>
      {!hasAny && (
        <Typography variant="body2" color="textSecondary">
          {translate(keys.enterAmountHint)}
        </Typography>
      )}
      {byRate.map(g =>
        row(g.rate, translate(keys.summaryVatRow, { rate: formatPercent(g.rate), amount: money(g.ht) }), money(g.vat))
      )}
      {hasMultiTax && (
        <>
          <Typography variant="caption" color="textSecondary">
            {translate(keys.multiRateNote)}
          </Typography>
          {multiTax.map(g => row(g.rates.join('_'), formatRates(g.rates), money(g.ttc)))}
        </>
      )}
      {!hasMultiTax && hasAny && (
        <>
          <Divider className={classes.divider} />
          {row('ht', translate(keys.totalHt), money(totalHT))}
          {row('vat', translate(keys.totalVat), money(totalVAT))}
        </>
      )}
      <Divider className={classes.divider} />
      <div aria-live="polite">{row('total', translate(keys.totalToCredit), money(totalTTC), true)}</div>
    </div>
  );
};

RefundSummary.propTypes = {
  classes: PropTypes.shape({}),
  translate: PropTypes.func.isRequired,
  summary: PropTypes.shape({
    byRate: PropTypes.arrayOf(PropTypes.shape({})),
    multiTax: PropTypes.arrayOf(PropTypes.shape({})),
    totalHT: PropTypes.number,
    totalVAT: PropTypes.number,
    totalTTC: PropTypes.number,
    hasMultiTax: PropTypes.bool,
  }).isRequired,
  locale: PropTypes.string,
  currency: PropTypes.string,
};

export const RefundSummaryTest = RefundSummary;

export default withStyles(styles)(RefundSummary);
